<div class="mx-auto w-full max-w-3xl p-4 sm:p-6">
  <div class="mb-6">
    <h1 class="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900">
      Initial Contact
    </h1>
    <p class="mt-2 text-sm sm:text-base text-zinc-600">
      Start by checking for an existing client to avoid duplicates.
    </p>
  </div>

  <div class="step-stage">
    @if (step() === 'lookup') {
      <div
        class="rounded-2xl border border-zinc-200 bg-white shadow-sm"
        animate.enter="ic-step-enter"
        animate.leave="ic-step-leave"
      >
      <div class="border-b border-zinc-200 px-5 py-4 sm:px-6">
        <h2 class="text-lg font-semibold text-zinc-900">Search for existing client</h2>
        <p class="mt-1 text-sm text-zinc-600">
          Enter the client’s last name and birthdate.
        </p>
      </div>

      <form
        [formGroup]="lookupForm"
        (ngSubmit)="submitLookup()"
        class="space-y-5 px-5 py-5 sm:px-6 sm:py-6"
      >
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label
              for="lookup-last-name"
              class="mb-1.5 block text-sm font-medium text-zinc-700"
            >
              Last Name
            </label>
            <input
              id="lookup-last-name"
              type="text"
              formControlName="lastName"
              class="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 outline-none transition focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200"
              placeholder="Enter last name"
            />

            @if (lookupForm.controls.lastName.touched && lookupForm.controls.lastName.invalid) {
              <p class="mt-1.5 text-sm text-red-600">
                Last name is required.
              </p>
            }
          </div>

          <div>
            <label
              for="lookup-birthdate"
              class="mb-1.5 block text-sm font-medium text-zinc-700"
            >
              Birthdate
            </label>
            <input
              id="lookup-birthdate"
              type="date"
              formControlName="birthdate"
              class="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 outline-none transition focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200"
            />

            @if (lookupForm.controls.birthdate.touched && lookupForm.controls.birthdate.invalid) {
              <p class="mt-1.5 text-sm text-red-600">
                Birthdate is required.
              </p>
            }
          </div>
        </div>

        @if (lookupError()) {
          <div class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {{ lookupError() }}
          </div>
        }

        <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="submit"
            [disabled]="searching()"
            class="inline-flex items-center justify-center rounded-xl bg-zinc-900 px-5 py-3 text-sm font-medium text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {{ searching() ? 'Searching...' : 'Search for existing client' }}
          </button>
        </div>
      </form>
      </div>
    }

    @if (step() === 'match-found') {
      <div class="space-y-4" animate.enter="ic-step-enter" animate.leave="ic-step-leave">
      <div class="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 sm:px-6">
        <h2 class="text-lg font-semibold text-amber-900">Possible match found</h2>
        <p class="mt-1 text-sm text-amber-800">
          Select the correct client below, or continue if this is not the right person.
        </p>
      </div>

      <div class="space-y-3">
        @for (match of matches(); track match.id) {
          <div class="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm sm:p-5">
            <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div class="min-w-0">
                <div class="text-base font-semibold text-zinc-900">
                  <span>{{ match.firstName }}</span>
                  @if (match.middleName) {
                    <span> {{ match.middleName }}</span>
                  }
                  <span> {{ match.lastName }}</span>
                </div>

                <div class="mt-1 text-sm text-zinc-600">
                  Birthdate:
                  <span class="font-medium text-zinc-800">{{ match.birthdate | date:'MMM d, y':'UTC' }}</span>
                </div>
              </div>

              <div class="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  (click)="openExistingClient(match.id)"
                  class="inline-flex items-center justify-center rounded-xl bg-zinc-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-zinc-800"
                >
                  Open Client Page
                </button>
              </div>
            </div>
          </div>
        }
      </div>

      <div class="rounded-2xl border border-zinc-200 bg-white px-5 py-4 shadow-sm sm:px-6">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div class="text-sm font-medium text-zinc-900">Not the right person?</div>
            <div class="mt-1 text-sm text-zinc-600">
              Create a new client profile using the entered last name and birthdate.
            </div>
          </div>

          <button
            type="button"
            (click)="continueToCreateProfile()"
            class="inline-flex items-center justify-center rounded-xl border border-zinc-300 bg-white px-4 py-2.5 text-sm font-medium text-zinc-800 transition hover:bg-zinc-50"
          >
            Create New Client
          </button>
        </div>
      </div>

      <div class="flex">
        <button
          type="button"
          (click)="goBackToLookup()"
          class="inline-flex items-center justify-center rounded-xl px-2 py-2 text-sm font-medium text-zinc-600 transition hover:text-zinc-900"
        >
          ← Back
        </button>
      </div>
      </div>
    }

    @if (step() === 'create-profile') {
      <div
        class="rounded-2xl border border-zinc-200 bg-white shadow-sm"
        animate.enter="ic-step-enter"
        animate.leave="ic-step-leave"
      >
      <div class="border-b border-zinc-200 px-5 py-4 sm:px-6">
        <h2 class="text-lg font-semibold text-zinc-900">Create Client Profile</h2>
        <p class="mt-1 text-sm text-zinc-600">
          Enter the client’s initial contact details. Last name and birthdate were carried over
          from the duplicate check.
        </p>
      </div>

      @if (noClientFoundMessage()) {
        <div class="mx-5 mt-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:mx-6">
          {{ noClientFoundMessage() }}
        </div>
      }

      <form
        [formGroup]="profileForm"
        (ngSubmit)="submitCreate()"
        class="space-y-6 px-5 py-5 sm:px-6 sm:py-6"
      >
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label
              for="client-unique-id"
              class="mb-1.5 block text-sm font-medium text-zinc-700"
            >
              Client Unique ID
            </label>
            <input
              id="client-unique-id"
              type="text"
              formControlName="clientUniqueId"
              class="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 outline-none transition focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200"
              placeholder="CL-XXXXXXXX"
            />

            @if (isRequiredFieldMissing(profileForm.controls.clientUniqueId)) {
              <p class="mt-1.5 text-sm text-red-600">
                Client unique ID is required.
              </p>
            }
          </div>

          <div>
            <label
              for="enrollment-date"
              class="mb-1.5 block text-sm font-medium text-zinc-700"
            >
              Enrollment Date
            </label>
            <input
              id="enrollment-date"
              type="date"
              formControlName="enrollmentDate"
              readonly
              class="w-full rounded-xl border border-zinc-300 bg-zinc-50 px-4 py-3 text-zinc-900 outline-none"
            />

            @if (isRequiredFieldMissing(profileForm.controls.enrollmentDate)) {
              <p class="mt-1.5 text-sm text-red-600">
                Enrollment date is required.
              </p>
            }
          </div>
        </div>

        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label
              for="first-name"
              class="mb-1.5 block text-sm font-medium text-zinc-700"
            >
              First Name
            </label>
            <input
              id="first-name"
              type="text"
              formControlName="firstName"
              class="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 outline-none transition focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200"
              placeholder="Enter first name"
            />

            @if (profileForm.controls.firstName.touched && profileForm.controls.firstName.invalid) {
              <p class="mt-1.5 text-sm text-red-600">
                First name is required.
              </p>
            }
          </div>

          <div>
            <label
              for="middle-name"
              class="mb-1.5 block text-sm font-medium text-zinc-700"
            >
              Middle Name
            </label>
            <input
              id="middle-name"
              type="text"
              formControlName="middleName"
              class="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 outline-none transition focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200"
              placeholder="Optional"
            />
          </div>

          <div>
            <label
              for="last-name"
              class="mb-1.5 block text-sm font-medium text-zinc-700"
            >
              Last Name
            </label>
            <input
              id="last-name"
              type="text"
              formControlName="lastName"
              class="w-full rounded-xl border border-zinc-300 bg-zinc-50 px-4 py-3 text-zinc-900 outline-none transition focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200"
              placeholder="Enter last name"
            />

            @if (profileForm.controls.lastName.touched && profileForm.controls.lastName.invalid) {
              <p class="mt-1.5 text-sm text-red-600">
                Last name is required.
              </p>
            }
          </div>

          <div>
            <label
              for="birthdate"
              class="mb-1.5 block text-sm font-medium text-zinc-700"
            >
              Birthdate
            </label>
            <input
              id="birthdate"
              type="date"
              formControlName="birthdate"
              class="w-full rounded-xl border border-zinc-300 bg-zinc-50 px-4 py-3 text-zinc-900 outline-none transition focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200"
            />

            @if (profileForm.controls.birthdate.touched && profileForm.controls.birthdate.invalid) {
              <p class="mt-1.5 text-sm text-red-600">
                Birthdate is required.
              </p>
            }
          </div>
        </div>

        <div>
          <label
            for="reason-for-initial-contact"
            class="mb-1.5 block text-sm font-medium text-zinc-700"
          >
            Reason for Initial Contact
          </label>
          <textarea
            id="reason-for-initial-contact"
            formControlName="reasonForInitialContact"
            rows="4"
            class="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 outline-none transition focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200"
            placeholder="Describe the client’s initial contact reason"
          ></textarea>

          @if (
            profileForm.controls.reasonForInitialContact.touched &&
            profileForm.controls.reasonForInitialContact.invalid
          ) {
            <p class="mt-1.5 text-sm text-red-600">
              Reason for initial contact is required.
            </p>
          }
        </div>

        <div>
          <h3 class="text-sm font-semibold uppercase tracking-wide text-zinc-500">
            Additional demographic details
          </h3>
          <p class="mt-1 text-sm text-zinc-600">
            These can support later reporting needs.
          </p>
        </div>

        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label for="phone" class="mb-1.5 block text-sm font-medium text-zinc-700">
              Phone
            </label>
            <input
              id="phone"
              type="text"
              formControlName="phone"
              class="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 outline-none transition focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200"
              placeholder="Optional"
            />
          </div>

          <div>
            <label for="email" class="mb-1.5 block text-sm font-medium text-zinc-700">
              Email
            </label>
            <input
              id="email"
              type="email"
              formControlName="email"
              class="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 outline-none transition focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200"
              placeholder="Optional"
            />

            @if (profileForm.controls.email.touched && profileForm.controls.email.invalid) {
              <p class="mt-1.5 text-sm text-red-600">
                Please enter a valid email address.
              </p>
            }
          </div>

          <div>
            <label for="race-2020" class="mb-1.5 block text-sm font-medium text-zinc-700">
              Race
            </label>
            <select
              id="race-2020"
              formControlName="race2020"
              class="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 outline-none transition focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200"
            >
              <option value="">Select race</option>
              @for (option of raceOptions; track option) {
                <option [value]="option">{{ option }}</option>
              }
            </select>

            @if (isRequiredFieldMissing(profileForm.controls.race2020)) {
              <p class="mt-1.5 text-sm text-red-600">
                Race is required.
              </p>
            }
          </div>

          <div>
            <label for="ethnicity-2020" class="mb-1.5 block text-sm font-medium text-zinc-700">
              Ethnicity
            </label>
            <select
              id="ethnicity-2020"
              formControlName="ethnicity2020"
              class="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 outline-none transition focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200"
            >
              <option value="">Select ethnicity</option>
              @for (option of ethnicityOptions; track option) {
                <option [value]="option">{{ option }}</option>
              }
            </select>

            @if (isRequiredFieldMissing(profileForm.controls.ethnicity2020)) {
              <p class="mt-1.5 text-sm text-red-600">
                Ethnicity is required.
              </p>
            }
          </div>

          <div>
            <label for="gender" class="mb-1.5 block text-sm font-medium text-zinc-700">
              Gender
            </label>
            <select
              id="gender"
              formControlName="gender"
              class="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 outline-none transition focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200"
            >
              <option value="">Select gender</option>
              @for (option of genderOptions; track option) {
                <option [value]="option">{{ option }}</option>
              }
            </select>

            @if (isRequiredFieldMissing(profileForm.controls.gender)) {
              <p class="mt-1.5 text-sm text-red-600">
                Gender is required.
              </p>
            }
          </div>

          @if (profileForm.controls.gender.getRawValue() === 'Other') {
            <div>
              <label for="gender-other-text" class="mb-1.5 block text-sm font-medium text-zinc-700">
                Gender (Other)
              </label>
              <input
                id="gender-other-text"
                type="text"
                formControlName="genderOtherText"
                class="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 outline-none transition focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200"
                placeholder="Please specify"
              />

              @if (isRequiredFieldMissing(profileForm.controls.genderOtherText)) {
                <p class="mt-1.5 text-sm text-red-600">
                  Please specify gender when Other is selected.
                </p>
              }
            </div>
          }

          <div>
            <label for="zip-code" class="mb-1.5 block text-sm font-medium text-zinc-700">
              Zip Code
            </label>
            <input
              id="zip-code"
              type="text"
              formControlName="zipCode"
              class="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 outline-none transition focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200"
              placeholder="Required"
            />

            @if (isRequiredFieldMissing(profileForm.controls.zipCode)) {
              <p class="mt-1.5 text-sm text-red-600">
                Zip code is required.
              </p>
            }
          </div>

          <div>
            <label for="consent-on-file" class="mb-1.5 block text-sm font-medium text-zinc-700">
              Consent on File to Refer-Out
            </label>
            <select
              id="consent-on-file"
              formControlName="consentOnFileToReferOut"
              class="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 outline-none transition focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200"
            >
              <option value="">Select consent option</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>

            @if (isRequiredFieldMissing(profileForm.controls.consentOnFileToReferOut)) {
              <p class="mt-1.5 text-sm text-red-600">
                Consent selection is required.
              </p>
            }
          </div>

          @if (profileForm.controls.consentOnFileToReferOut.getRawValue() === 'yes') {
            <div>
              <label for="consent-date" class="mb-1.5 block text-sm font-medium text-zinc-700">
                Date of Consent
              </label>
              <input
                id="consent-date"
                type="date"
                formControlName="consentOnFileToReferOutDate"
                class="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 outline-none transition focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200"
              />

              @if (isRequiredFieldMissing(profileForm.controls.consentOnFileToReferOutDate)) {
                <p class="mt-1.5 text-sm text-red-600">
                  Consent date is required when consent is on file.
                </p>
              }
            </div>
          }
        </div>

        @if (createError()) {
          <div class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {{ createError() }}
          </div>
        }

        <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
          <button
            type="button"
            (click)="goBackToLookup()"
            class="inline-flex items-center justify-center rounded-xl border border-zinc-300 bg-white px-4 py-3 text-sm font-medium text-zinc-800 transition hover:bg-zinc-50"
          >
            Back
          </button>

          <button
            type="submit"
            [disabled]="creating()"
            class="inline-flex items-center justify-center rounded-xl bg-zinc-900 px-5 py-3 text-sm font-medium text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {{ creating() ? 'Creating client...' : 'Create Client' }}
          </button>
        </div>
      </form>
      </div>
    }
  </div>
</div>